import { Tab, Tabs } from '@material-ui/core'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { createInvoiceAction, getCartDetails, getGradeDetails } from 'api'
import BaseEntity from 'app/components/BaseEntity'
import Button from 'app/components/generic/Button'
import ButtonGroup from 'app/components/generic/ButtonGroup'
import { PageTabsBar } from 'app/components/generic/PageLayout'
import ModalForm from 'app/components/ModalForm'
import { navigationPaths, tableColumnDefaultOption } from 'app/config'
import constants from 'app/constants'
import { eventAlert, eventEmitter } from 'app/helpers'
import {
  fetchInstallmentOrdersData,
  fetchOrdersData,
  fetchPlayCareTransactionsData
} from 'app/store/actions/ordersEntity'

import CartDetailsTable from './CartDetailsTable'
import { columns as installmentColumns } from './columns/installment'
import { columns as playCareColumns } from './columns/playCare'
import { columns as regularColumns } from './columns/regular'
import OrdersCollectForm from './OrdersCollectForm'

const TAB_BY_PATH = {
  [navigationPaths.orders]: 'regular',
  [navigationPaths.installmentOrders]: 'instalment',
  [navigationPaths.dropInCareOrders]: 'dropInCare'
}

const PATH_BY_TAB = {
  regular: navigationPaths.orders,
  instalment: navigationPaths.installmentOrders,
  dropInCare: navigationPaths.dropInCareOrders
}

const Orders = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()
  const currentTab = TAB_BY_PATH[pathname] || 'regular'

  const ordersData = useSelector(
    (state) => state.ordersEntityReducer.ordersData
  )
  const installmentOrdersData = useSelector(
    (state) => state.ordersEntityReducer.installmentOrdersData
  )
  const playCareOrdersData = useSelector(
    (state) => state.ordersEntityReducer.playCareOrdersData
  )

  const [isCollectModalOpen, setIsCollectModalOpen] = React.useState(false)
  const [editIndex, setEditIndex] = React.useState(null)
  const [cartDetailsOpen, setCartDetailsOpen] = React.useState(false)
  const [cartDetails, setCartDetails] = React.useState(null)
  const [loadingCartDetails, setLoadingCartDetails] = React.useState(false)
  const [grades, setGrades] = React.useState({})

  React.useEffect(() => {
    if (currentTab === 'instalment') {
      getGradeDetails().then((res) => setGrades(res))
    }
  }, [currentTab])

  const fetchCartDetails = React.useCallback(async ({ invoiceId }) => {
    setLoadingCartDetails(true)
    try {
      const response = await getCartDetails({ invoiceId })
      setCartDetails(response)
      setCartDetailsOpen(true)
    } catch (error) {
      console.error('Failed to fetch cart details:', error)
      eventAlert('Failed to fetch cart details', constants.ERROR)
    } finally {
      setLoadingCartDetails(false)
    }
  }, [])

  const openCollectModal = React.useCallback((e, dataIndex) => {
    e.preventDefault()
    setIsCollectModalOpen(true)
    setEditIndex(dataIndex)
  }, [])

  const closeAllModals = () => {
    setIsCollectModalOpen(false)
    setCartDetailsOpen(false)
    setCartDetails(null)
  }

  const handleCollectSave = async (newData) => {
    const dataset =
      currentTab === 'instalment' ? installmentOrdersData : ordersData
    const fetchAction =
      currentTab === 'instalment' ? fetchInstallmentOrdersData : fetchOrdersData
    const entityConstant =
      currentTab === 'instalment'
        ? constants.INSTALLMENT_ORDERS
        : constants.ORDERS

    try {
      await createInvoiceAction({
        ...newData,
        invoice: dataset.data[editIndex].uuid
      })
      eventEmitter.emit(constants.CLOSE_MODAL_FORM)
      eventAlert('Done', constants.SUCCESS)
      closeAllModals()
      dispatch(fetchAction({}, false, false, entityConstant))
    } catch (error) {
      console.log(error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR, error)
      eventAlert(error.message, constants.ERROR)
    }
  }

  const regularColumnsWithActions = React.useMemo(() => {
    return [
      ...regularColumns.slice(0, 9),
      {
        name: '',
        label: 'cart',
        options: {
          disableFilter: true,
          customBodyRenderLite: (dataIndex) => {
            const invoiceId = ordersData?.data?.[dataIndex]?.uuid
            return (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  fetchCartDetails({ invoiceId })
                }}
              >
                view details
              </a>
            )
          }
        }
      },
      ...regularColumns.slice(9),
      {
        name: '',
        options: {
          customBodyRenderLite: (dataIndex) => {
            const cartType = ordersData?.data?.[dataIndex]?.type
            const cartStatus = ordersData?.data?.[dataIndex]?.status
            const showCollectButton =
              (cartType === 'topup_booking' ||
                cartType === 'topup' ||
                cartType === 'booking') &&
              cartStatus === 'pending'
            return showCollectButton ? (
              <ButtonGroup>
                <Button onClick={(e) => openCollectModal(e, dataIndex)}>
                  Collect
                </Button>
              </ButtonGroup>
            ) : null
          }
        }
      }
    ]
  }, [ordersData, fetchCartDetails, openCollectModal])

  const installmentColumnsWithActions = React.useMemo(() => {
    // Feature 7b inserted a `Late fee` column at index 6 (after value),
    // shifting the Grade-insertion site from index 8 → 9.
    return [
      ...installmentColumns.slice(0, 9),
      {
        name: 'grade.uuid',
        label: 'Grade',
        widget: 'select',
        widgetFilter: true,
        isValueKey: true,
        valueLabelMappings: grades,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'standard_uuid',
          customBodyRender: (value) => <span>{grades[value]}</span>
        }
      },
      ...installmentColumns.slice(9),
      {
        name: '',
        options: {
          customBodyRenderLite: (dataIndex) => {
            const cartStatus = installmentOrdersData?.data?.[dataIndex]?.status
            const showCollectButton = cartStatus === 'pending'
            return showCollectButton ? (
              <ButtonGroup>
                <Button onClick={(e) => openCollectModal(e, dataIndex)}>
                  Collect
                </Button>
              </ButtonGroup>
            ) : null
          }
        }
      }
    ]
  }, [installmentOrdersData, grades, openCollectModal])

  const handleTabChange = (_, value) => {
    if (value !== currentTab) history.push(PATH_BY_TAB[value])
  }

  const renderTabContent = () => {
    switch (currentTab) {
      case 'regular':
        return (
          <BaseEntity
            entity={constants.ORDERS}
            label={constants.ORDERS_LABEL}
            fetchData={fetchOrdersData}
            data={ordersData}
            columns={regularColumnsWithActions}
            readOnly
          />
        )
      case 'instalment':
        return (
          <BaseEntity
            entity={constants.INSTALLMENT_ORDERS}
            label={constants.INSTALLMENT_ORDERS_LABEL}
            fetchData={fetchInstallmentOrdersData}
            data={installmentOrdersData}
            columns={installmentColumnsWithActions}
            readOnly
          />
        )
      case 'dropInCare':
        return (
          <BaseEntity
            entity={constants.PLAY_CARE_ORDERS}
            label={constants.PLAY_CARE_ORDERS_LABEL}
            fetchData={fetchPlayCareTransactionsData}
            data={playCareOrdersData}
            columns={playCareColumns}
            readOnly
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <PageTabsBar>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Regular" value="regular" />
          <Tab label="Instalment" value="instalment" />
          <Tab label="Drop-In Care" value="dropInCare" />
        </Tabs>
      </PageTabsBar>
      {renderTabContent()}
      <OrdersCollectForm
        isModalOpen={isCollectModalOpen}
        onSaveClick={handleCollectSave}
        closeModal={closeAllModals}
        action="collect"
      />
      <ModalForm
        containerStyles={{ borderRadius: 10 }}
        className="border"
        isModalOpen={cartDetailsOpen}
        onModalClose={closeAllModals}
        enableSave={false}
        modalTitle="Cart Details"
      >
        {loadingCartDetails ? (
          <p>Loading cart details...</p>
        ) : (
          <CartDetailsTable data={cartDetails} />
        )}
      </ModalForm>
    </>
  )
}

export default Orders
